import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Loader2, ArrowLeft } from 'lucide-react';
import { archiveService, ArchivePost } from '../../services/archive.service';
import { setSelectedOutfit as setReduxSelectedOutfit, setSelectedImageIndex, clearSelectedOutfit } from '../../store/slices/tryOnSlice';
import { RootState } from '../../store';

interface CreatorsStudioModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const CreatorsStudioModal: React.FC<CreatorsStudioModalProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const selectedOutfit = useSelector((state: RootState) => state.tryOn.selectedOutfit);
  const selectedImageIndex = useSelector((state: RootState) => state.tryOn.selectedImageIndex);
  const [archivePosts, setArchivePosts] = useState<ArchivePost[]>([]);
  const [isLoadingPosts, setIsLoadingPosts] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMorePosts, setHasMorePosts] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);
  
  // Detailed view state
  const [designerPosts, setDesignerPosts] = useState<ArchivePost[]>([]);

  // Fetch archive posts for templates
  const fetchArchivePosts = useCallback(async (page: number, append: boolean = false) => {
    try {
      if (append) {
        setIsLoadingMore(true);
      } else {
        setIsLoadingPosts(true);
      }
      
      const response = await archiveService.getFeed({
        page,
        limit: 12,
        status: 'published'
      });
      
      if (response.success && response.data?.items) {
        if (append) {
          setArchivePosts(prev => [...prev, ...response.data.items]);
        } else {
          setArchivePosts(response.data.items);
        }
        
        const { page: currentPageNum, totalPages } = response.data.pagination;
        setHasMorePosts(currentPageNum < totalPages);
      }
    } catch (error) {
      console.error('Failed to fetch archive posts:', error);
    } finally {
      setIsLoadingPosts(false);
      setIsLoadingMore(false);
    }
  }, []);

  // Fetch posts when modal opens
  useEffect(() => {
    if (isOpen && archivePosts.length === 0) {
      fetchArchivePosts(1);
    }
  }, [isOpen, archivePosts.length, fetchArchivePosts]);

  // Handle scroll for infinite loading
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    const scrollPercentage = (target.scrollTop + target.clientHeight) / target.scrollHeight;
    
    if (scrollPercentage > 0.8 && !isLoadingMore && hasMorePosts && !searchQuery.trim()) {
      setCurrentPage(prev => prev + 1);
      fetchArchivePosts(currentPage + 1, true);
    }
  };

  const handleOutfitSelect = async (post: ArchivePost) => {
    const outfit = {
      images: post.images.map(img => img.imageUrl),
      creator: post.user.fullName,
      postId: post.id,
      creatorAvatar: post.user.profilePicture || undefined
    };
    dispatch(setReduxSelectedOutfit(outfit));
    
    // Fetch more posts from this designer
    try {
      const response = await archiveService.getFeed({
        page: 1,
        limit: 6,
        status: 'published',
        userId: post.user.id
      });
      
      if (response.success && response.data?.items) {
        // Filter out the currently selected post
        const otherPosts = response.data.items.filter(p => p.id !== post.id);
        setDesignerPosts(otherPosts);
      }
    } catch (error) {
      console.error('Failed to fetch designer posts:', error);
    }
  };

  const handleBack = () => {
    // Clear the selected outfit in Redux
    dispatch(clearSelectedOutfit());
  };

  const handleProceed = () => {
    if (selectedOutfit) {
      // Outfit is already in Redux, just close the modal
      // If not on creators-studio page, navigate there
      if (!window.location.pathname.includes('/creators-studio')) {
        navigate('/creators-studio');
      }
      onClose();
    }
  };

  if (!isOpen) return null;

  // Show detailed view if outfit is selected
  if (selectedOutfit) {
    return (
      <div className="space-y-4">
        {/* Back Button */}
        <button
          onClick={handleBack}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
        >
          <ArrowLeft size={18} />
          <span className="font-medium text-sm">Back to Gallery</span>
        </button>

        {/* Selected Outfit Details */}
        <div className="space-y-4">
          {/* Creator Info */}
          <div className="flex items-center gap-3">
            {selectedOutfit.creatorAvatar ? (
              <img
                src={selectedOutfit.creatorAvatar}
                alt={selectedOutfit.creator}
                className="w-10 h-10 rounded-full object-cover"
              />
            ) : (
              <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
                <span className="text-white text-sm font-bold">
                  {selectedOutfit.creator?.charAt(0).toUpperCase() || 'U'}
                </span>
              </div>
            )}
            <div>
              <p className="font-semibold text-gray-900 text-sm">{selectedOutfit.creator || 'User'}</p>
              <p className="text-xs text-gray-500">Shared this look</p>
            </div>
          </div>

          {/* Main Image */}
          <div className="relative">
            <img
              src={selectedOutfit.images[selectedImageIndex]}
              alt="Selected outfit"
              className="w-full aspect-[3/4] object-cover rounded-xl"
            />
            
            {/* Image counter badge */}
            {selectedOutfit.images.length > 1 && (
              <div className="absolute top-3 right-3 px-3 py-1.5 bg-black/70 backdrop-blur-md rounded-full text-white text-xs font-semibold">
                {selectedImageIndex + 1} / {selectedOutfit.images.length}
              </div>
            )}
          </div>

          {/* Image selector - only show if multiple images */}
          {selectedOutfit.images.length > 1 && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-semibold text-gray-900">Select Image to Try On</h4>
                <span className="text-xs text-gray-500">{selectedOutfit.images.length} photos</span>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {selectedOutfit.images.map((img, index) => (
                  <button
                    key={index}
                    onClick={() => dispatch(setSelectedImageIndex(index))}
                    className={`relative aspect-square rounded-lg overflow-hidden transition-all ${
                      selectedImageIndex === index
                        ? 'ring-2 ring-gray-900 ring-offset-2 scale-95'
                        : 'hover:scale-95 opacity-70 hover:opacity-100'
                    }`}
                  >
                    <img
                      src={img}
                      alt={`Outfit ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                    {selectedImageIndex === index && (
                      <div className="absolute inset-0 bg-gradient-to-t from-gray-900/60 to-transparent flex items-end justify-center pb-1">
                        <span className="text-white text-xs font-bold">✓ Selected</span>
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* More from this designer */}
          {designerPosts.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-gray-900 mb-2">More from {selectedOutfit.creator}</h4>
              <div className="grid grid-cols-2 gap-2">
                {designerPosts.slice(0, 4).map((post) => {
                  const imageUrls = post.images.map(img => img.imageUrl);
                  return (
                    <button
                      key={post.id}
                      onClick={() => handleOutfitSelect(post)}
                      className="relative aspect-square rounded-lg overflow-hidden bg-gray-100 hover:ring-2 hover:ring-gray-900 hover:ring-offset-1 transition-all"
                    >
                      <img
                        src={imageUrls[0]}
                        alt={`${post.user.fullName}'s outfit`}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Proceed Button */}
          <button
            onClick={handleProceed}
            className="w-full py-3 bg-gray-900 hover:bg-gray-800 text-white font-semibold rounded-xl transition-all"
          >
            Continue to Try-On
          </button>
        </div>
      </div>
    );
  }

  // Show gallery view
  return (
    <div className="space-y-4">
      {/* Search Bar */}
      <div className="relative">
        <input
          type="text"
          placeholder="Search by designer or style..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full px-4 py-2.5 pl-10 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-600 focus:border-transparent text-sm transition-all"
        />
        <svg
          className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
          />
        </svg>
      </div>

      {/* Gallery Grid */}
      <div 
        ref={scrollRef}
        onScroll={handleScroll}
        className="max-h-[60vh] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100"
      >
        {isLoadingPosts ? (
          <div className="grid grid-cols-2 gap-3">
            {[...Array(6)].map((_, index) => (
              <div key={index} className="aspect-[3/4] rounded-lg bg-gray-200 animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {archivePosts
              .filter((post) => {
                if (!searchQuery.trim()) return true;
                const query = searchQuery.toLowerCase();
                return (
                  post.user.fullName.toLowerCase().includes(query) ||
                  post.caption?.toLowerCase().includes(query) ||
                  post.tags.some(tag => tag.toLowerCase().includes(query))
                );
              })
              .map((post) => {
                const imageUrls = post.images.map(img => img.imageUrl);
                const imageCount = post.images.length;
                
                return (
                  <button
                    key={post.id}
                    onClick={() => handleOutfitSelect(post)}
                    className="group relative aspect-[3/4] rounded-lg overflow-hidden bg-gray-100 hover:ring-2 hover:ring-purple-600 hover:ring-offset-1 transition-all"
                  >
                    <img
                      src={imageUrls[0]}
                      alt={`${post.user.fullName}'s outfit`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    
                    {/* Image count badge */}
                    {imageCount > 1 && (
                      <div className="absolute top-2 right-2 px-2 py-1 bg-black/70 backdrop-blur-sm rounded-full text-white text-xs font-semibold">
                        {imageCount}
                      </div>
                    )}
                    
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="absolute bottom-0 left-0 right-0 p-3">
                        <p className="text-white text-xs font-semibold">Try This On</p>
                      </div>
                    </div>
                  </button>
                );
              })}
            
            {/* Loading more indicator */}
            {isLoadingMore && (
              <div className="col-span-2 flex justify-center py-4">
                <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
              </div>
            )}
            
            {/* End of results */}
            {!hasMorePosts && archivePosts.length > 0 && !searchQuery.trim() && (
              <div className="col-span-2 text-center py-4 text-sm text-gray-500">
                No more outfits to load
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CreatorsStudioModal;
